import { Division, GetScore, Lifter, Meet } from "types";
import { toKg } from "util/conversions";
import { isLbsMeet } from "util/meetHelper";
import { competitionCode } from "util/standardDivisions";
import { ageCoef } from "./age";
import { getTotal } from "util/lifterHelper";

const equipmentLevelConversionMap = {
  RAW: "RAW",
  CLASSIC_RAW: "RAW",
  RAW_WITH_WRAPS: "RAW",
  RAW_CLASSIC: "RAW",
  RAW_MODERN: "RAW",
  RAW_AMATEUR: "RAW",
  RAW_PRO: "RAW",
  EQUIPPED: "EQUIPPED",
  SINGLE_PLY: "EQUIPPED",
  MULTI_PLY: "EQUIPPED",
  UNLIMITED: "EQUIPPED",
  MULTIPLY: "EQUIPPED",
  EQUIPPED_AMATEUR: "EQUIPPED",
  EQUIPPED_PRO: "EQUIPPED",
} as const;

//                   Mean                                           Standard Deviation
//                   a           b         c             d          a          b           c                 d
// Men Raw 3-Lift    35.40516    3.117635  53.59224      669.3207   82.06514   5.949887    108.2303          141.4961
// Men Raw Bench     -127860600  1.226138  0.0006475945  194.4717   17.762     2.556199    175.1132          69.44957
// Men EQ 3-Lift     -1174.41    1.138728  22.02526      969.8907   124526800  0.78621955  0.000001387957    213.3635
// Men EQ Bench      -15.30516   1.961268  66.10145      243.4878   13.69912   5.56231     70.95444          50.30335
// Women Raw 3-Lift  -335445400  2.385459  0.09435324    368.2169   -6.290097  0.4146666   3771620000        117932.3
// Women Raw Bench   -6891452    1.923637  0.02336547    78.48406   0.6422216  8.737411    0.00000005819191  26.28695
// Women EQ 3-Lift   -337593400  1.102888  0.0001299085  535.7992   69.57924   4.970095    98.39708          116.99
// Women EQ Bench    26.67261    2.50796   58.98913      119.992    20.18229   9.877279    80.84458          32.82745

const zScoreParameters = {
  MALE: {
    RAW: {
      PL: {
        mean: { a: 35.40516, b: 3.117635, c: 53.59224, d: 669.3207 },
        standardDeviation: {
          a: 82.06514,
          b: 5.949887,
          c: 108.2303,
          d: 141.4961,
        },
      },
      BP: {
        mean: { a: -127860600, b: 1.226138, c: 0.0006475945, d: 194.4717 },
        standardDeviation: { a: 17.762, b: 2.556199, c: 175.1132, d: 69.44957 },
      },
    },
    EQUIPPED: {
      PL: {
        mean: { a: -1174.41, b: 1.138728, c: 22.02526, d: 969.8907 },
        standardDeviation: {
          a: 124526800,
          b: 0.78621955,
          c: 0.000001387957,
          d: 213.3635,
        },
      },
      BP: {
        mean: { a: -15.30516, b: 1.961268, c: 66.10145, d: 243.4878 },
        standardDeviation: {
          a: 13.69912,
          b: 5.56231,
          c: 70.95444,
          d: 50.30335,
        },
      },
    },
  },
  FEMALE: {
    RAW: {
      PL: {
        mean: { a: -335445400, b: 2.385459, c: 0.09435324, d: 368.2169 },
        standardDeviation: {
          a: -6.290097,
          b: 0.4146666,
          c: 3771620000,
          d: 117932.3,
        },
      },
      BP: {
        mean: { a: -6891452, b: 1.923637, c: 0.02336547, d: 78.48406 },
        standardDeviation: {
          a: 0.6422216,
          b: 8.737411,
          c: 0.00000005819191,
          d: 26.28695,
        },
      },
    },
    EQUIPPED: {
      PL: {
        mean: { a: -337593400, b: 1.102888, c: 0.0001299085, d: 535.7992 },
        standardDeviation: { a: 69.57924, b: 4.970095, c: 98.39708, d: 116.99 },
      },
      BP: {
        mean: { a: 26.67261, b: 2.50796, c: 58.98913, d: 119.992 },
        standardDeviation: {
          a: 20.18229,
          b: 9.877279,
          c: 80.84458,
          d: 32.82745,
        },
      },
    },
  },
};
export const zScore = (
  total: number,
  division: Division | undefined,
  lifter: Lifter,
  meet: Meet
) => {
  if (total <= 0 || !division) {
    return 0;
  }

  const compCode = competitionCode(division);

  if (compCode !== "PL" && compCode !== "BP") {
    return 0;
  }

  let bodyWeight = lifter.bodyWeight;

  if (!bodyWeight) {
    return 0;
  }

  if (!lifter.gender) {
    return 0;
  }

  if (lifter.gender === "MX") {
    return 0;
  }

  if (isLbsMeet(meet)) {
    bodyWeight = toKg(bodyWeight);
  }

  const equipmentLevel = equipmentLevelConversionMap[division.rawOrEquipped];
  if (!equipmentLevel) {
    return 0;
  }

  const p = zScoreParameters[lifter.gender][equipmentLevel][compCode];

  if (!p) {
    return 0;
  }

  // Mean = d + (a -d)/(1 + (Bwt/c)^b)
  const mean =
    p.mean.d +
    (p.mean.a - p.mean.d) / (1 + Math.pow(bodyWeight / p.mean.c, p.mean.b));

  // Standard Deviation = d + (a - d)/(1 + (Bwt/c)^b)
  const standardDeviation =
    p.standardDeviation.d +
    (p.standardDeviation.a - p.standardDeviation.d) /
      (1 + Math.pow(bodyWeight / p.standardDeviation.c, p.standardDeviation.b));

  // Z-Score = 5 + (Total or Bench - Mean) / Standard Deviation
  return 5 + (total - mean) / standardDeviation;
};

export const getZscorePoints: GetScore = ({
  lifter,
  division,
  meet,
  forecasted,
}) => {
  let total = getTotal({ lifter, division, meet, forecasted });
  if (isLbsMeet(meet)) {
    total = toKg(total);
  }

  return zScore(total, division, lifter, meet);
};

export const getZscoreAndAgePoints: GetScore = ({
  lifter,
  division,
  meet,
  forecasted,
}) => {
  let total = getTotal({ lifter, division, meet, forecasted });
  if (isLbsMeet(meet)) {
    total = toKg(total);
  }

  const ageCoefMultiple = ageCoef(lifter, meet);

  const adjustedTotal = total * ageCoefMultiple;

  return zScore(adjustedTotal, division, lifter, meet);
};
